import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { posApi, formatApiError } from '@/lib/api';
import CustomerActionPanel from '@/components/pos/CustomerActionPanel';
import { CameraScanBox } from '@/components/shared/CameraScanBox';
import { useScanMode } from '@/hooks/useScanMode';
import { toast } from 'sonner';
import { ArrowLeft, ScanLine, Camera } from 'lucide-react';

// Hardware USB/Bluetooth barcode scanners act as a keyboard: they type the token then send Enter.
export default function PosScanner() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const handledRef = useRef(false);
  const [scanSession, setScanSession] = useState(0);
  const [scanMode, setScanMode] = useScanMode();
  const [customer, setCustomer] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!customer && scanMode === 'SCANNER') {
      handledRef.current = false;
      inputRef.current?.focus();
    }
  }, [customer, scanMode, scanSession]);

  const handleScan = async (token) => {
    try {
      const { data } = await posApi.post('/qr/scan', { token });
      setCustomer(data.customer);
      setSubscription(data.subscription);
    } catch (err) {
      toast.error(formatApiError(err));
      setScanSession((s) => s + 1);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const token = e.target.value.trim();
    e.target.value = '';
    if (!token || handledRef.current) return;
    handledRef.current = true;
    handleScan(token);
  };

  return (
    <div className="min-h-screen bg-obsidian px-6 py-8 flex flex-col items-center">
      <div className="w-full max-w-md flex items-center gap-3 mb-6">
        <button data-testid="pos-scanner-back-btn" onClick={() => navigate('/pos/home')} className="text-white/60 hover:text-white">
          <ArrowLeft size={22} />
        </button>
        <h1 className="font-heading text-xl font-semibold text-white">Scan QR</h1>
      </div>

      <div className="w-full max-w-md">
        {!customer && (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-2 flex items-center justify-center gap-1 border-b border-white/10" data-testid="pos-scan-mode-toggle">
              <button
                data-testid="pos-scan-mode-camera-btn"
                onClick={() => setScanMode('CAMERA')}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                  scanMode === 'CAMERA' ? 'bg-saffron text-white' : 'text-white/50 hover:bg-white/10'
                }`}
              >
                <Camera size={14} /> Camera
              </button>
              <button
                data-testid="pos-scan-mode-scanner-btn"
                onClick={() => setScanMode('SCANNER')}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                  scanMode === 'SCANNER' ? 'bg-saffron text-white' : 'text-white/50 hover:bg-white/10'
                }`}
              >
                <ScanLine size={14} /> Handheld Scanner
              </button>
            </div>

            {scanMode === 'CAMERA' ? (
              <CameraScanBox onScan={handleScan} scanSession={scanSession} />
            ) : (
              <div className="p-10 flex flex-col items-center text-center gap-3">
                <ScanLine size={44} className="text-saffron" />
                <p className="text-white/70 text-sm">Ready — scan the customer's QR code with your handheld scanner</p>
                <input
                  ref={inputRef}
                  data-testid="pos-hardware-scanner-input"
                  autoFocus
                  onKeyDown={onKeyDown}
                  className="opacity-0 h-1 w-1 absolute"
                />
              </div>
            )}
          </div>
        )}

        {customer && (
          <CustomerActionPanel
            customer={customer}
            subscription={subscription}
            onReset={() => {
              setCustomer(null);
              setSubscription(null);
              setScanSession((s) => s + 1);
            }}
          />
        )}
      </div>
    </div>
  );
}
